import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Clock, ShoppingCart } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProductDto } from "@gaole/shared";
import { customerOrderStatusLabel, customerOrderStatusColorKey } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { rm, fmtDate } from "../lib/format";
import { creditPackageImageUrl, DEFAULT_COIN_ICON } from "../lib/credits";
import { toast } from "../stores/toast";
import { Countdown } from "../components/Countdown";
import { ProductImage, productImageSrc } from "../components/ProductCard";
import { ListingTitle } from "../components/ListingTitle";
import { ImageHoverPreview } from "../components/ImageHoverPreview";
import { Badge, Button, EmptyState, PageLoader, statusColor } from "../components/ui";
import { PageHeader } from "../components/storefront/PageHeader";
import { marketplaceShopPath } from "../lib/marketplaceShopPath";
import { MOBILE_STICKY_BAR_PAD } from "../components/shell/MobileStickyBar";
import {
  type CartShopKey,
  keysForSellerGroup,
  keysForStore,
  listingLineKey,
  packLineKey,
  productLineKey,
  shopKeyForSeller,
} from "../lib/cartSelection";
import { isActionablePendingPayment } from "../lib/pendingPayment";

type CartTab = "cart" | "pending";

type MarketplaceCartGroup = {
  sellerId: string;
  shopName: string;
  shopSlug: string;
  items: {
    id: string;
    quantity: number;
    listing: {
      id: string;
      slug: string;
      title: string;
      price: number;
      quantity: number;
      images: string[];
    };
  }[];
  subtotal: number;
  shippingFee: number;
  total: number;
};

interface CartData {
  items: { id: string; quantity: number; product: ProductDto; discount: number; appliedRules: string[] }[];
  packItems: {
    id: string;
    quantity: number;
    pack: {
      id: string;
      name: string;
      slug: string;
      images: string[];
      price: number;
      creditPrice: number | null;
      totalRemaining: number;
    };
    cashTotal: number;
    creditTotal: number;
  }[];
  subtotal: number;
  discountTotal: number;
  shippingFee: number;
  shippingCountry?: string | null;
  shippingState?: string | null;
  total: number;
  grandTotal?: number;
  marketplace?: { groups: MarketplaceCartGroup[]; subtotal: number; shippingTotal: number; total: number } | null;
  marketplaceSubtotal?: number;
  marketplaceShipping?: number;
}

interface PendingOrder {
  id: string;
  orderNo: string;
  kind: string;
  status: string;
  total: number;
  creditUsed: number;
  paymentDeadline: string | null;
  createdAt: string;
  latestReceiptStatus: string | null;
  items: { id: string; name: string; quantity: number }[];
}

const KIND_LABEL: Record<string, string> = {
  STORE: "Shop",
  RAFFLE_WIN: "Raffle win",
  AUCTION_WIN: "Auction win",
  CREDIT_TOPUP: "Credit top-up",
  VAULT_SHIPMENT: "Vault shipment",
};

function receiptHint(status: string | null): string | null {
  if (status === "PENDING") return "Receipt under review";
  if (status === "REJECTED") return "Receipt rejected — upload again";
  return null;
}

function pendingOrderHref(order: PendingOrder): string {
  if (order.kind === "RAFFLE_WIN" || order.kind === "AUCTION_WIN") {
    return `/checkout/order/${order.id}`;
  }
  return `/orders/${order.id}`;
}

function CartSelectCheckbox({
  checked,
  indeterminate,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <input
      type="checkbox"
      aria-label={ariaLabel}
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = Boolean(indeterminate);
      }}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 h-4 w-4 shrink-0 rounded border-white/25 bg-white/5 accent-brand-pink"
    />
  );
}

function selectedStoreTotal(
  data: CartData,
  cartItems: CartData["items"],
  cartPackItems: CartData["packItems"],
  selected: Set<string>,
): number {
  const storeKeys = keysForStore(
    cartItems.map((i) => i.product.id),
    cartPackItems.map((p) => p.pack.id),
  );
  if (storeKeys.length === 0) return 0;
  const allSelected = storeKeys.every((k) => selected.has(k));
  if (allSelected) return data.total;
  let merch = 0;
  for (const item of cartItems) {
    if (!selected.has(productLineKey(item.product.id))) continue;
    merch += item.product.price * item.quantity - item.discount;
  }
  for (const item of cartPackItems) {
    if (!selected.has(packLineKey(item.pack.id))) continue;
    merch += item.cashTotal;
  }
  return merch;
}

function selectedMarketGroupTotal(group: MarketplaceCartGroup, selected: Set<string>): number {
  const keys = keysForSellerGroup(group.items.map((row) => row.listing.id));
  if (keys.every((k) => selected.has(k))) return group.total;
  return group.items.reduce((sum, row) => {
    if (!selected.has(listingLineKey(row.listing.id))) return sum;
    return sum + row.listing.price * row.quantity;
  }, 0);
}

export function Cart() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<CartTab>(() => (searchParams.get("tab") === "pending" ? "pending" : "cart"));
  const [activeShop, setActiveShop] = useState<CartShopKey | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const selectionInit = useRef(false);

  const { data, isLoading: cartLoading } = useQuery({
    queryKey: ["cart"],
    queryFn: () => api<CartData>("/cart"),
  });

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ["orders"],
    queryFn: () => api<{ items: PendingOrder[] }>("/orders/mine"),
  });

  const pendingOrders = (ordersData?.items ?? []).filter(isActionablePendingPayment);
  const cartItems = data?.items ?? [];
  const cartPackItems = data?.packItems ?? [];
  const marketGroups = data?.marketplace?.groups ?? [];
  const marketLineCount = marketGroups.reduce((n, g) => n + g.items.length, 0);
  const storeLineCount = cartItems.length + cartPackItems.length;
  const lineCount = storeLineCount + marketLineCount;
  async function updateQty(productId: string, quantity: number) {
    try {
      const next = await api<CartData>(`/cart/items/${productId}`, { method: "PUT", body: { quantity } });
      queryClient.setQueryData(["cart"], next);
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't update cart");
    }
  }

  async function updateMarketQty(listingId: string, quantity: number) {
    try {
      await api(`/marketplace/cart/items/${listingId}`, { method: "PUT", body: { quantity } });
      await queryClient.invalidateQueries({ queryKey: ["cart"] });
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't update cart");
    }
  }

  useEffect(() => {
    if (!data || selectionInit.current) return;
    if (lineCount === 0) return;
    selectionInit.current = true;
    if (storeLineCount > 0) {
      setActiveShop("store");
      setSelected(
        new Set(
          keysForStore(
            cartItems.map((i) => i.product.id),
            cartPackItems.map((p) => p.pack.id),
          ),
        ),
      );
    } else if (marketGroups[0]) {
      const sellerId = marketGroups[0].sellerId;
      setActiveShop(shopKeyForSeller(sellerId));
      setSelected(new Set(keysForSellerGroup(marketGroups[0].items.map((row) => row.listing.id))));
    }
  }, [data, lineCount, storeLineCount, cartItems, cartPackItems, marketGroups]);

  function selectShop(shop: CartShopKey, keys: string[]) {
    setActiveShop(shop);
    setSelected(new Set(keys));
  }

  function toggleLine(shop: CartShopKey, key: string) {
    if (activeShop !== shop) {
      setActiveShop(shop);
      setSelected(new Set([key]));
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleShop(shop: CartShopKey, shopKeys: string[], checked: boolean) {
    if (checked) selectShop(shop, shopKeys);
    else if (activeShop === shop) {
      setSelected(new Set());
    }
  }

  function storeCheckoutUrl(): string {
    const productIds = cartItems
      .filter((i) => selected.has(productLineKey(i.product.id)))
      .map((i) => i.product.id);
    const packIds = cartPackItems.filter((p) => selected.has(packLineKey(p.pack.id))).map((p) => p.pack.id);
    const allStoreKeys = keysForStore(
      cartItems.map((i) => i.product.id),
      cartPackItems.map((p) => p.pack.id),
    );
    const allSelected = allStoreKeys.every((k) => selected.has(k));
    if (allSelected) return "/checkout?store=1";
    const params = new URLSearchParams({ store: "1" });
    params.set("products", productIds.join(","));
    params.set("packs", packIds.join(","));
    return `/checkout?${params.toString()}`;
  }

  function marketCheckoutUrl(group: MarketplaceCartGroup): string {
    const listingIds = group.items
      .filter((row) => selected.has(listingLineKey(row.listing.id)))
      .map((row) => row.listing.id);
    const allKeys = keysForSellerGroup(group.items.map((row) => row.listing.id));
    const allSelected = allKeys.every((k) => selected.has(k));
    if (allSelected) return `/checkout?sellers=${encodeURIComponent(group.sellerId)}`;
    const params = new URLSearchParams({ sellers: group.sellerId });
    params.set("listings", listingIds.join(","));
    return `/checkout?${params.toString()}`;
  }

  async function updatePackQty(packId: string, quantity: number) {
    try {
      const next = await api<CartData>(`/cart/packs/items/${packId}`, { method: "PUT", body: { quantity } });
      queryClient.setQueryData(["cart"], next);
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't update cart");
    }
  }

  if (cartLoading || ordersLoading) return <PageLoader />;

  return (
    <div className={`space-y-4 px-4 pt-4 ${MOBILE_STICKY_BAR_PAD} md:pb-8`}>
      <PageHeader
        title="Cart"
        description="Review items and pending payments before checkout."
      />

      <div className="flex gap-1.5 rounded-full bg-surface p-1">
        <button
          type="button"
          onClick={() => setTab("cart")}
          className={`relative flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
            tab === "cart" ? "bg-gradient-brand text-white" : "text-white/50"
          }`}
        >
          Cart
          {lineCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-pink px-1 text-[9px] font-bold">
              {lineCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setTab("pending")}
          className={`relative flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
            tab === "pending" ? "bg-gradient-brand text-white" : "text-white/50"
          }`}
        >
          Pending payment
          {pendingOrders.length > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] font-bold text-brand-dark">
              {pendingOrders.length}
            </span>
          )}
        </button>
      </div>

      {tab === "cart" ? (
        lineCount === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title="Your cart is empty"
            hint="Singles, bundles and packs land here."
            action={
              pendingOrders.length > 0 ? (
                <Button variant="outline" onClick={() => setTab("pending")}>
                  View pending payment
                </Button>
              ) : (
                <Link to="/shop">
                  <Button>Browse the shop</Button>
                </Link>
              )
            }
          />
        ) : (
          <>
            <div className="space-y-4">
              {storeLineCount > 0 && (() => {
                const storeKeys = keysForStore(
                  cartItems.map((i) => i.product.id),
                  cartPackItems.map((p) => p.pack.id),
                );
                const storeAllSelected = storeKeys.length > 0 && storeKeys.every((k) => selected.has(k));
                const storeSomeSelected = storeKeys.some((k) => selected.has(k));
                const storeActive = activeShop === "store";
                return (
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2 px-1">
                    <CartSelectCheckbox
                      checked={storeActive && storeAllSelected}
                      indeterminate={storeActive && storeSomeSelected && !storeAllSelected}
                      onChange={(checked) => toggleShop("store", storeKeys, checked)}
                      ariaLabel="Select all Mr Gaole items"
                    />
                    <span className="font-display text-sm font-bold text-white">Mr Gaole</span>
                    <span className="rounded-full bg-brand-pink/15 px-2 py-0.5 text-[10px] font-semibold text-brand-pink">
                      Official store
                    </span>
                  </div>
              {cartPackItems.map((item) => {
                const lineKey = packLineKey(item.pack.id);
                const lineSelected = storeActive && selected.has(lineKey);
                return (
                <div key={item.id} className="flex gap-3 rounded-2xl border border-white/5 bg-surface p-3">
                  <CartSelectCheckbox
                    checked={lineSelected}
                    onChange={() => toggleLine("store", lineKey)}
                    ariaLabel={`Select ${item.pack.name}`}
                  />
                  <Link to={`/packs/${item.pack.id}`}>
                    <ProductImage
                      src={item.pack.images[0] ?? null}
                      alt=""
                      className="h-[72px] w-[72px] shrink-0 rounded-xl"
                    />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <ListingTitle className="font-semibold text-white">{item.pack.name}</ListingTitle>
                    <p className="text-[10px] text-white/45">Mystery pack · {item.quantity} open{item.quantity > 1 ? "s" : ""}</p>
                    <p className="mt-0.5 font-display text-sm font-bold text-brand-pink">{rm(item.cashTotal)}</p>
                    {item.quantity > item.pack.totalRemaining && (
                      <p className="text-[10px] text-amber-400">Only {item.pack.totalRemaining} opens left</p>
                    )}
                    <div className="mt-1.5 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updatePackQty(item.pack.id, item.quantity - 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-sm active:bg-white/15"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm font-semibold">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => updatePackQty(item.pack.id, item.quantity + 1)}
                        disabled={item.quantity >= item.pack.totalRemaining}
                        className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-sm active:bg-white/15 disabled:opacity-30"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        onClick={() => updatePackQty(item.pack.id, 0)}
                        className="ml-auto text-[11px] text-white/40 active:text-red-400"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              );})}
              {cartItems.map((item) => {
                const isCreditPack =
                  item.product.type === "CREDIT_PACK" || item.product.behavior === "CREDIT_PACK";
                const thumbSrc = isCreditPack
                  ? productImageSrc(item.product) ?? DEFAULT_COIN_ICON
                  : productImageSrc(item.product);
                const lineKey = productLineKey(item.product.id);
                const lineSelected = storeActive && selected.has(lineKey);
                return (
                <div key={item.id} className="flex gap-3 rounded-2xl border border-white/5 bg-surface p-3">
                  <CartSelectCheckbox
                    checked={lineSelected}
                    onChange={() => toggleLine("store", lineKey)}
                    ariaLabel={`Select ${item.product.name}`}
                  />
                  {isCreditPack ? (
                    <ProductImage
                      src={thumbSrc}
                      alt=""
                      backgroundColor={item.product.imageBackgroundColor}
                      className="h-[72px] w-[72px] shrink-0 rounded-xl"
                    />
                  ) : (
                  <Link to={`/shop/${item.product.slug}`}>
                    {thumbSrc ? (
                      <ImageHoverPreview src={thumbSrc} alt={item.product.name} previewWidth={240} previewHeight={240}>
                        <ProductImage
                          src={thumbSrc}
                          alt=""
                          backgroundColor={item.product.imageBackgroundColor}
                          className="h-[72px] w-[72px] shrink-0 rounded-xl"
                        />
                      </ImageHoverPreview>
                    ) : (
                      <ProductImage
                        src={null}
                        alt=""
                        backgroundColor={item.product.imageBackgroundColor}
                        className="h-[72px] w-[72px] shrink-0 rounded-xl"
                      />
                    )}
                  </Link>
                  )}
                  <div className="min-w-0 flex-1">
                    <ListingTitle className="font-semibold text-white">{item.product.name}</ListingTitle>
                    <p className="mt-0.5 font-display text-sm font-bold text-brand-pink">{rm(item.product.price)}</p>
                    {item.discount > 0 && (
                      <p className="text-[10px] text-emerald-400">−{rm(item.discount)} discount applied</p>
                    )}
                    {!isCreditPack && item.quantity > item.product.stockAvailable && (
                      <p className="text-[10px] text-amber-400">Only {item.product.stockAvailable} in stock</p>
                    )}
                    {isCreditPack ? (
                      <div className="mt-1.5 flex items-center justify-between">
                        <span className="text-[10px] text-white/45">1 pack · change selection in Wallet</span>
                        <button
                          type="button"
                          onClick={() => updateQty(item.product.id, 0)}
                          className="text-[11px] text-white/40 active:text-red-400"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                    <div className="mt-1.5 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateQty(item.product.id, item.quantity - 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-sm active:bg-white/15"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm font-semibold">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => updateQty(item.product.id, item.quantity + 1)}
                        disabled={item.quantity >= item.product.stockAvailable}
                        className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-sm active:bg-white/15 disabled:opacity-30"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        onClick={() => updateQty(item.product.id, 0)}
                        className="ml-auto text-[11px] text-white/40 active:text-red-400"
                      >
                        Remove
                      </button>
                    </div>
                    )}
                  </div>
                </div>
                );
              })}
                  {storeActive && storeSomeSelected && data ? (
                    <Button className="w-full" onClick={() => navigate(storeCheckoutUrl())}>
                      Checkout Mr Gaole · {rm(selectedStoreTotal(data, cartItems, cartPackItems, selected))}
                    </Button>
                  ) : null}
                </div>
              );})()}

              {marketGroups.map((group) => {
                const shop = shopKeyForSeller(group.sellerId);
                const groupKeys = keysForSellerGroup(group.items.map((row) => row.listing.id));
                const groupAllSelected = groupKeys.every((k) => selected.has(k));
                const groupSomeSelected = groupKeys.some((k) => selected.has(k));
                const groupActive = activeShop === shop;
                return (
                <div key={group.sellerId} className="space-y-2.5">
                  <div className="flex items-center gap-2 px-1">
                    <CartSelectCheckbox
                      checked={groupActive && groupAllSelected}
                      indeterminate={groupActive && groupSomeSelected && !groupAllSelected}
                      onChange={(checked) => toggleShop(shop, groupKeys, checked)}
                      ariaLabel={`Select all items from ${group.shopName}`}
                    />
                    <Link
                      to={marketplaceShopPath(group.shopSlug)}
                      className="font-display text-sm font-bold text-white hover:text-brand-pink"
                    >
                      {group.shopName}
                    </Link>
                  </div>
                  {group.items.map((row) => {
                    const max = row.listing.quantity;
                    const href = `/market/${row.listing.slug}?shop=${encodeURIComponent(group.shopSlug)}`;
                    const lineKey = listingLineKey(row.listing.id);
                    const lineSelected = groupActive && selected.has(lineKey);
                    return (
                      <div key={row.id} className="flex gap-3 rounded-2xl border border-white/5 bg-surface p-3">
                        <CartSelectCheckbox
                          checked={lineSelected}
                          onChange={() => toggleLine(shop, lineKey)}
                          ariaLabel={`Select ${row.listing.title}`}
                        />
                        <Link to={href} className="shrink-0">
                          <ProductImage src={row.listing.images[0] ?? null} alt="" className="h-[72px] w-[72px] rounded-xl" />
                        </Link>
                        <div className="min-w-0 flex-1">
                          <Link to={href} className="line-clamp-2 text-sm font-semibold text-white hover:text-brand-pink">
                            {row.listing.title}
                          </Link>
                          <p className="mt-0.5 font-display text-sm font-bold text-brand-pink">{rm(row.listing.price)}</p>
                          {row.quantity > max && (
                            <p className="text-[10px] text-amber-400">Only {max} left</p>
                          )}
                          <div className="mt-1.5 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => updateMarketQty(row.listing.id, row.quantity - 1)}
                              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-sm active:bg-white/15"
                            >
                              −
                            </button>
                            <span className="w-6 text-center text-sm font-semibold">{row.quantity}</span>
                            <button
                              type="button"
                              onClick={() => updateMarketQty(row.listing.id, row.quantity + 1)}
                              disabled={row.quantity >= max}
                              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-sm active:bg-white/15 disabled:opacity-30"
                            >
                              +
                            </button>
                            <button
                              type="button"
                              onClick={() => updateMarketQty(row.listing.id, 0)}
                              className="ml-auto text-[11px] text-white/40 active:text-red-400"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {groupActive && groupSomeSelected ? (
                    <>
                      <p className="px-1 text-[10px] text-white/40">
                        {groupAllSelected
                          ? `Subtotal ${rm(group.subtotal)}${
                              group.shippingFee > 0
                                ? ` · est. shipping ${rm(group.shippingFee)}`
                                : " · shipping calculated at checkout"
                            }`
                          : "Shipping calculated at checkout for selected items"}
                      </p>
                      <Button
                        className="w-full"
                        onClick={() => navigate(marketCheckoutUrl(group))}
                      >
                        Checkout {group.shopName} · {rm(selectedMarketGroupTotal(group, selected))}
                      </Button>
                    </>
                  ) : null}
                </div>
              );})}
            </div>

          </>
        )
      ) : pendingOrders.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="No pending payments"
          hint="Orders awaiting payment will show up here after checkout."
          action={
            <Link to="/shop">
              <Button variant="outline">Browse the shop</Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-2.5">
          {pendingOrders.map((order) => {
            const hint = receiptHint(order.latestReceiptStatus);
            return (
              <Link
                key={order.id}
                to={pendingOrderHref(order)}
                className="block rounded-2xl border border-amber-400/20 bg-surface p-3.5 active:scale-[0.99]"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] text-white/50">{order.orderNo}</span>
                  <Badge color={statusColor(customerOrderStatusColorKey(order.status, order.latestReceiptStatus))}>
                    {customerOrderStatusLabel(order.status, order.latestReceiptStatus)}
                  </Badge>
                </div>
                <ListingTitle className="mt-1.5 font-semibold text-white">
                  {`${order.items[0]?.name ?? KIND_LABEL[order.kind] ?? order.kind}${
                    order.items.length > 1 ? ` +${order.items.length - 1} more` : ""
                  }`}
                </ListingTitle>
                <div className="mt-1 flex items-center justify-between text-[11px]">
                  <span className="text-white/40">
                    {KIND_LABEL[order.kind] ?? order.kind} · {fmtDate(order.createdAt)}
                  </span>
                  <span className="font-display font-bold text-brand-pink">
                    {order.creditUsed > 0 ? `${order.creditUsed} cr` : rm(order.total)}
                  </span>
                </div>
                {order.paymentDeadline && (
                  <div className="mt-2.5 flex items-center justify-between rounded-xl border border-amber-400/20 bg-amber-500/10 px-2.5 py-2">
                    <span className="text-[10px] font-medium text-amber-200/90">Pay before</span>
                    <Countdown
                      target={order.paymentDeadline}
                      compact
                      onDone={() => void queryClient.invalidateQueries({ queryKey: ["orders"] })}
                    />
                  </div>
                )}
                {hint && <p className="mt-2 text-[10px] font-medium text-amber-300/90">{hint}</p>}
                <p className="mt-2 text-[10px] font-semibold text-brand-pink">Pay or upload receipt →</p>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
